import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { getMovieById, getRelatedMovies } from "@/utils/movieUtils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useFavorites } from "@/hooks/useFavorites";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [activeEpisode, setActiveEpisode] = useState(1);
  const [toast, setToast] = useState("");
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    setActiveEpisode(1);
  }, [id]);

  useDocumentTitle({
    title: movie ? `${movie.title} - ${movie.typeLabel} - 屁屁影院` : "影视详情-屁屁影院",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}，${movie.genres.join("、")}）：${movie.synopsis} 导演${movie.director}，主演${movie.actors.join("、")}。`
      : "屁屁影院影视详情页。",
    keywords: movie ? `屁屁影院,${movie.title},${movie.genres.join(",")},在线观看${movie.title}` : "屁屁影院,影视详情",
  });

  const related = useMemo(() => (movie ? getRelatedMovies(movie, 6) : []), [movie]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-film-line text-3xl" />
          </span>
          <h1 className="text-2xl font-bold text-foreground-950">内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">您访问的影视作品可能已下架或链接有误。</p>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-950 hover:bg-background-100"
            >
              返回上一页
            </button>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-accent-600"
            >
              回到首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const episodes = movie.episodes ? Array.from({ length: movie.episodes }, (_, i) => i + 1) : [];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("分享链接：" + url);
    }
  };

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "年份", value: String(movie.year) },
    { label: "片长/状态", value: movie.duration },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <div className="relative h-[260px] w-full overflow-hidden md:h-[420px]">
          <img
            src={movie.backdrop}
            alt={`${movie.title}剧照`}
            title={`${movie.title} ${movie.genres.join(" ")}`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/20" />
        </div>

        <div className="mx-auto -mt-24 max-w-[1280px] px-4 md:-mt-40 md:px-6">
          <nav className="mb-4 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${movie.typeLabel === "电影" ? "film" : "tv"}`} className="hover:text-accent-400">
              {movie.typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="truncate text-foreground-950">{movie.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-lg border border-background-200/60 md:mx-0 md:w-56">
              <img
                src={movie.cover}
                alt={`${movie.title}封面`}
                title={`${movie.title} ${movie.genres.join(" ")}`}
                className="h-full w-full object-cover object-top"
                style={{ aspectRatio: "2 / 3" }}
              />
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-foreground-950 md:text-4xl">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" />
                  <strong className="font-heading text-base text-accent-400">{movie.rating.toFixed(1)}</strong>
                </span>
                <span className="text-background-400">|</span>
                <span>{movie.year}</span>
                <span className="text-background-400">|</span>
                <span>{movie.region}</span>
                <span className="text-background-400">|</span>
                <span>{movie.duration}</span>
                <span className="text-background-400">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-fill text-accent-500" />
                  {movie.views}万
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{g}</span>
                ))}
                {movie.tags.map((t) => (
                  <span key={t} className="rounded-full bg-accent-500/15 px-3 py-1 text-xs text-accent-300">{t}</span>
                ))}
              </div>

              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-500">{movie.synopsis}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setToast("即将支持播放功能")}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-circle-fill text-xl" />
                  立即播放
                </button>
                <button
                  type="button"
                  onClick={() => {
                    toggle(movie.id);
                    setToast(favorited ? "已取消收藏" : "已加入收藏");
                  }}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                    favorited
                      ? "border-accent-500 bg-accent-500/10 text-accent-400"
                      : "border-background-300 text-foreground-950 hover:bg-background-100"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
                >
                  <i className="ri-share-line" />
                  分享
                </button>
              </div>
              <p className="mt-3 flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-information-line" />
                提示：本站播放功能为前端演示，正式播放能力即将上线。
              </p>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="mb-4 text-lg font-bold text-foreground-950">影片信息</h2>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200/60 bg-background-200/60 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 bg-background-100 px-4 py-3 text-sm">
                  <span className="w-20 shrink-0 text-foreground-500">{row.label}</span>
                  <span className="flex-1 text-foreground-950">{row.value}</span>
                </div>
              ))}
            </div>
          </section>

          {episodes.length > 0 && (
            <section className="mt-12">
              <h2 className="mb-4 text-lg font-bold text-foreground-950">
                选集播放
                <span className="ml-2 text-sm font-normal text-foreground-500">共 {episodes.length} 集</span>
              </h2>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {episodes.map((ep) => (
                  <button
                    key={ep}
                    type="button"
                    onClick={() => {
                      setActiveEpisode(ep);
                      setToast(`已切换到第 ${ep} 集`);
                    }}
                    className={`shrink-0 whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors ${
                      activeEpisode === ep
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200 bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
              <p className="mt-3 rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-500">
                当前第 <strong className="text-accent-400">{activeEpisode}</strong> 集简介：{movie.synopsis}（第 {activeEpisode} 集将带来更多精彩剧情，播放功能即将支持。）
              </p>
            </section>
          )}

          <section className="mt-12">
            <SectionHeading title="相关推荐" subtitle="根据类型与风格为你精选" icon="ri-magic-line" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>

          <div className="mt-10 mb-12">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-950 hover:bg-background-100"
            >
              <i className="ri-arrow-left-line" />
              返回
            </button>
          </div>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-fade-in rounded-lg bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 shadow-none">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}